'use client'

import { Box, Flex, IconButton, Text, VStack } from '@chakra-ui/react'

import { useRouterState } from '@tanstack/react-router'
import { LogOutIcon, Menu, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pathMatchesNavPath } from '@/shared/utils/navPathMatch'
import { getNavData } from './navData'
import type { INavData } from './navData'
import { NavItem } from './NavItem'
import { useAuthStore } from '@/shared/store/authStore'
import { useAbilityStore } from '@/shared/store/abilityStore'
import { NavBranch } from './NavBranch'
import { ReportViolationLink } from './ReportViolationLink'
import { useSidebarStore } from '../../shared/store/sidebarStore'
import { sidebarStyles } from './styles'
import { config } from '@/shared'

export function Sidebar() {
  const { t } = useTranslation()
  const open = useSidebarStore((s) => s.open)
  const toggleOpen = useSidebarStore((s) => s.toggleOpen)
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const currentSearch: Record<string, unknown> = routerState.location.search
  const navItems = getNavData()
  const abilities = useAbilityStore((s) => s.abilities)
  const { clearUser } = useAuthStore()

  const handleLogout = () => {
    clearUser()

    window.location.href = `${config.SSO_LOGOUT_URL}?client_id=${config.SSO_CLIENT_ID}&post_logout_redirect_uri=${window.location.origin + '/auth/entry'}`
  }

  const bottomNavItems: INavData[] = [
    { key: 'settings', icon: Settings, path: '/settings' },
  ] as const

  return (
    <Box
      as="aside"
      w={open ? '240px' : '72px'}
      css={{
        backgroundImage: sidebarStyles.bg,
        _dark: { backgroundImage: sidebarStyles.bgDark },
      }}
      borderWidth={{ base: '0px', _dark: '1px' }}
      borderStyle="solid"
      borderColor={{ base: 'transparent', _dark: sidebarStyles.borderDark }}
      display="flex"
      flexDirection="column"
      position="sticky"
      top={sidebarStyles.inset}
      ml={sidebarStyles.inset}
      my={sidebarStyles.inset}
      h={`calc(100vh - ${sidebarStyles.inset} * 2)`}
      borderRadius={sidebarStyles.radius}
      boxShadow={{
        base: sidebarStyles.shadow,
        _dark: sidebarStyles.shadowDark,
      }}
      overflow="hidden"
      alignSelf="flex-start"
      flexShrink={0}
      zIndex={20}
      transition="width 0.2s ease"
    >
      {/* Toggle + brand */}
      <Flex
        align="center"
        justify={open ? 'flex-start' : 'center'}
        gap={3}
        h="72px"
        px={open ? 4 : 2}
        flexShrink={0}
      >
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('nav.toggleSidebar')}
          color={sidebarStyles.fg}
          _hover={{ bg: sidebarStyles.hoverBg, color: sidebarStyles.fgActive }}
          onClick={toggleOpen}
        >
          <Menu size={20} />
        </IconButton>
        {open ? (
          <Flex
            align="center"
            justify="center"
            flex={1}
            h="40px"
            borderRadius="lg"
            bg="rgba(0, 0, 0, 0.18)"
          >
            <Text
              fontSize="11px"
              fontWeight="semibold"
              letterSpacing="0.14em"
              color={sidebarStyles.fg}
            >
              LOGO
            </Text>
          </Flex>
        ) : null}
      </Flex>

      {/* Main navigation */}
      <VStack
        gap={1}
        flex={1}
        px={open ? 3 : 2}
        py={2}
        align="stretch"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
          },
        }}
      >
        <NavBranch
          open={open}
          items={navItems}
          currentPath={currentPath}
          currentSearch={currentSearch}
          abilities={abilities}
          t={t}
        />
      </VStack>

      <ReportViolationLink open={open} />

      {/* Bottom navigation */}
      <VStack
        gap={1}
        px={open ? 3 : 2}
        pt={2}
        pb={4}
        align="stretch"
        flexShrink={0}
      >
        {bottomNavItems.map((item: INavData) => (
          <NavItem
            open={open}
            key={item.key}
            onClick={item.onClick}
            icon={item.icon}
            label={t(`nav.${item.key}`)}
            path={item.path}
            active={
              item.path ? pathMatchesNavPath(currentPath, item.path) : false
            }
          />
        ))}
        <NavItem
          open={open}
          key={'logout'}
          onClick={handleLogout}
          icon={LogOutIcon}
          label={t(`nav.logout`)}
          path={'/logout'}
          active={currentPath === '/logout'}
        />
      </VStack>
    </Box>
  )
}
